// src/components/analytics/RevenueTrendChart.js
import React from "react";
import { Card, CardHeader, CardContent, Box, Typography } from "@material-ui/core";
import {
  Chart,
  ArgumentAxis,
  ValueAxis,
  AreaSeries,
  ScatterSeries,
  Tooltip,
} from "@devexpress/dx-react-chart-material-ui";
// ✅ Behaviors & scales come from the core package:
import { EventTracker, Animation, ArgumentScale } from "@devexpress/dx-react-chart";
import { scaleTime } from "d3-scale";

const currencyFmt = (v, code = "USD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: code, maximumFractionDigits: 0 }).format(v || 0);
const formatDate = (value) => {
  if (!value) return "Unknown date";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(+date)) return "Unknown date";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

export default function RevenueTrendChart({
  title = "Revenue Trend",
  subheader,
  series = [],
  currency = "USD",
  totalRevenue = 0,
  mode = "cash",
  grouping = "day",
}) {
  // Ensure Dates, numbers, and sorted by time
  const data = React.useMemo(
    () =>
      (series || [])
        .map(d => ({
          ...d,
          period: new Date(d.period),
          revenue: Number(d.revenue) || 0,
          payments: (d.payments || []).map(p => ({
            ...p,
            date: p.date ? new Date(p.date) : null,
            amount: Number(p.amount) || 0,
          })),
          invoiceDetails: (d.invoiceDetails || []).map(inv => ({
            ...inv,
            clientStore: inv.clientStore || inv.clientName || null,
            dueDate: inv.dueDate ? new Date(inv.dueDate) : null,
            receivedByDate: inv.receivedByDate ? new Date(inv.receivedByDate) : null,
            amount: Number(inv.amount) || 0,
          })),
        }))
        .sort((a, b) => +a.period - +b.period),
    [series]
  );
  const [tooltipTarget, setTooltipTarget] = React.useState(null);
  const firstPeriod = data[0]?.period?.getTime() ?? null;
  const lastPeriod = data[data.length - 1]?.period?.getTime() ?? null;

  React.useEffect(() => {
    // Reset tooltip selection whenever the underlying data slice changes.
    setTooltipTarget(null);
  }, [firstPeriod, lastPeriod, data.length]);

  const chartKey = data.length ? `${firstPeriod}-${lastPeriod}-${data.length}` : "empty-series";

  return (
    <Card variant="outlined">
      <CardHeader
        title={
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <span>{title}</span>
            <Box textAlign="right">
              <Typography variant="caption" color="textSecondary" style={{ letterSpacing: 0.2 }}>
                Total Revenue
              </Typography>
              <Typography variant="h6" style={{ fontWeight: 600 }}>
                {currencyFmt(totalRevenue, currency)}
              </Typography>
            </Box>
          </Box>
        }
        subheader={subheader}
      />
      <CardContent style={{ height: 340 }}>
        {data.length === 0 ? (
          <Box style={{ color: "rgba(0,0,0,.54)" }}>No data in this range.</Box>
        ) : (
          <Box style={{ width: "100%", height: 300 }}>
            <Chart data={data} height={300} key={chartKey}>
              {/* 👇 This line fixes the crash by defining a time scale for Date arguments */}
              <ArgumentScale factory={scaleTime} />

              <ArgumentAxis
                tickFormat={() => (val) =>
                  new Date(val).toLocaleDateString(undefined, { month: "short", day: "numeric" })
                }
              />
              <ValueAxis />

              <AreaSeries valueField="revenue" argumentField="period" />
              <ScatterSeries
                name="Payments"
                valueField="revenue"
                argumentField="period"
              />

              <EventTracker />
              <Tooltip
                targetItem={tooltipTarget}
                onTargetItemChange={setTooltipTarget}
                contentComponent={({ targetItem }) => {
                  const idx = targetItem?.point ?? 0;
                  const seriesName = targetItem?.series || "";
                  const p = data[idx] || { period: new Date(), revenue: 0 };
                  const payments = p.payments || [];
                  const invoices = p.invoiceDetails || [];
                  const isAccrual = mode === "accrual";
                  const isMonthly = grouping === "month";

                  if (isMonthly) {
                    return (
                      <Box style={{ padding: 8 }}>
                        <div style={{ fontSize: 12, opacity: 0.7 }}>
                          {formatDate(p.period)}
                        </div>
                        <div style={{ fontWeight: 700 }}>
                          {currencyFmt(p.revenue, currency)}
                        </div>
                      </Box>
                    );
                  }

                  return (
                    <Box style={{ padding: 8 }}>
                      <div style={{ fontSize: 12, opacity: 0.7 }}>
                        {formatDate(p.period)}
                      </div>
                      {seriesName === "Payments" && !isAccrual ? (
                        <Box>
                          <div style={{ fontWeight: 700 }}>Payments ({payments.length})</div>
                          {payments.length === 0 ? (
                            <Typography variant="caption" color="textSecondary">No payment details.</Typography>
                          ) : (
                            payments.map((pay, payIdx) => (
                              <Box key={payIdx} mt={0.5}>
                                <Typography variant="body2" style={{ fontWeight: 600 }}>
                                  {pay.paidBy || "Payment"}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {currencyFmt(pay.amount, currency)}
                                  {pay.paymentMethod ? ` • ${pay.paymentMethod}` : ""}
                                  {pay.date ? ` • ${formatDate(pay.date)}` : ""}
                                </Typography>
                                {pay.note && (
                                  <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                                    {pay.note}
                                  </Typography>
                                )}
                              </Box>
                            ))
                          )}
                        </Box>
                      ) : isAccrual ? (
                        <Box>
                          <div style={{ fontWeight: 700 }}>Invoices ({invoices.length})</div>
                          {invoices.length === 0 ? (
                            <Typography variant="caption" color="textSecondary">No invoice details.</Typography>
                          ) : (
                            invoices.map((inv, idx) => (
                              <Box key={idx} mt={0.5}>
                                <Typography variant="body2" style={{ fontWeight: 600 }}>
                                  {inv.clientStore || "Invoice"}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {currencyFmt(inv.amount, currency)}
                                  {inv.dueDate ? ` • Due ${formatDate(inv.dueDate)}` : ""}
                                </Typography>
                              </Box>
                            ))
                          )}
                        </Box>
                      ) : (
                        <div style={{ fontWeight: 700 }}>{currencyFmt(p.revenue, currency)}</div>
                      )}
                    </Box>
                  );
                }}
              />
              <Animation />
            </Chart>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
